import type { Racer } from '../../game/Racer';
import type { LapTimer } from '../../timing/LapTimer';
import type { GhostShip } from '../../game/GhostShip';
import type { WrongWayDetector } from '../../game/WrongWayDetector';
import type { OffTrackBudget } from '../../game/OffTrackBudget';
import type { RaceMode } from '../../game/DrivingAssists';
import type { activePhysics } from '../../game/physics-versions';
import type { System } from '../ServiceLocator';

export type RuntimeMode = 'title' | 'racing' | 'paused';

/**
 * Holder for the core race entities + the running race mode + per-tick
 * counters. Construction stays in bootstrap because the entities are
 * built at different points in the boot pipeline and many capture
 * closures over other bootstrap-local state. Future PRs can migrate
 * the state-machine transition bodies (t.toTitle / t.toRacing /
 * t.toPaused / t.fromPaused) into RaceSystem when those bodies can be
 * decoupled from non-race side effects (audio, DRS, shadows,
 * wakeLock, race-music timing).
 *
 * For now this is a discoverable slot that holds the references e2e
 * tests and the diagnostics surface need, plus mode/counter tracking.
 */
export class RaceSystem implements System {
  readonly name = 'RaceSystem';

  racer!: Racer;
  physics!: ReturnType<typeof activePhysics>;
  lapTimer!: LapTimer;
  ghostShip!: GhostShip;
  wrongWay?: WrongWayDetector;
  offTrackBudget?: OffTrackBudget;

  mode: RuntimeMode = 'title';
  raceMode: RaceMode = 'standard';

  stepCount = 0;
  renderedFrameCount = 0;

  incStep(): void { this.stepCount += 1; }
  incRender(): void { this.renderedFrameCount += 1; }

  setRaceMode(next: RaceMode): void {
    this.raceMode = next;
  }

  /** Fields this system contributes to the window.__twin debug surface. */
  debugEntries(): Record<string, unknown> {
    return {
      racer: this.racer,
      physics: this.physics,
      lapTimer: this.lapTimer,
      ghostShip: this.ghostShip,
      getRaceMode: (): RaceMode => this.raceMode,
      setRaceMode: this.setRaceMode.bind(this),
      getStepCount: (): number => this.stepCount,
      getRenderedFrameCount: (): number => this.renderedFrameCount,
    };
  }
}
